import React from 'react';
import ModalButton from 'components/ModalButton/ModalButton';
import CommonLargeModal from 'components/Modals/CommonLargeModal';
import useAddMember, { MIN_DATE_OF_BIRTH, NAME_MAX_LENGTH, TITLE_OPTIONS } from 'views/MyRac/ExpiryRenewal/hooks/useAddMember';
import useEditMember from 'views/MyRac/ExpiryRenewal/hooks/useEditMember';
import { Member } from 'types/expiryRenewal';
import clsx from 'clsx';
import TextInput from '../../atoms/TextInput/TextInput';
import styles from './MemberModal.module.scss';

/**
 * The add hook's return surface is the form's contract, so useEditMember is obliged to
 * return the same keys - the same arrangement VehicleForm has with useAddVehicle.
 */
type MemberFormProps = ReturnType<typeof useAddMember> & {
  title: string;
  submitLabel: string;
  onClose: () => void;
};

const MemberForm: React.FC<MemberFormProps> = ({
  fields,
  errors,
  touched,
  isSaveDisabled,
  handleChange,
  handleBlur,
  handleSave,
  title,
  submitLabel,
  onClose,
}) => {
  // A date of birth can never be in the future, so stop the picker there too.
  const today = new Date().toISOString().slice(0, 10);
  const dateOfBirthError = touched.DateOfBirth && errors.DateOfBirth ? errors.DateOfBirth : undefined;
  const titleError = touched.Title && errors.Title ? errors.Title : undefined;

  return (
    <CommonLargeModal
      show
      onHide={onClose}
      title={<span style={{ color: '#47474A' }}>{title}</span>}
      titleClassName="font-bold-display fw-normal fs-3"
      bodyClassName=""
      bodyStyle={{ textAlign: 'center' }}
    >
      <form noValidate onSubmit={handleSave} style={{ margin: '0 auto', maxWidth: '510px' }}>
        <div className={styles.wrapper}>
          <div className={clsx(styles.field, titleError && styles.hasError)}>
            {titleError && <span className={styles.floatingLabel}>{titleError}</span>}
            <select
              id="Title"
              name="Title"
              value={fields.Title}
              aria-label="Title"
              onChange={handleChange}
              onBlur={() => handleBlur('Title')}
              className={clsx(styles.select, !fields.Title && styles.isPlaceholder)}
            >
              {/*
                "hidden" keeps the placeholder out of the open list while still labelling the
                closed control. Without it the placeholder is both selected and disabled, so the
                browser aligns the popup with a disabled row under the cursor and releasing the
                mouse there dismisses the list without selecting anything.
              */}
              <option value="" disabled hidden>Title</option>
              {TITLE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>

        <TextInput
          name="FirstName"
          label="First name"
          value={fields.FirstName}
          error={touched.FirstName && errors.FirstName ? errors.FirstName : undefined}
          maxLength={NAME_MAX_LENGTH}
          onChange={handleChange}
          onBlur={() => handleBlur('FirstName')}
        />

        <TextInput
          name="LastName"
          label="Last name"
          value={fields.LastName}
          error={touched.LastName && errors.LastName ? errors.LastName : undefined}
          maxLength={NAME_MAX_LENGTH}
          onChange={handleChange}
          onBlur={() => handleBlur('LastName')}
        />

        <div className={styles.wrapper}>
          <div className={clsx(styles.field, dateOfBirthError && styles.hasError)}>
            {dateOfBirthError && <span className={styles.floatingLabel}>{dateOfBirthError}</span>}
            <input
              id="DateOfBirth"
              name="DateOfBirth"
              type="date"
              value={fields.DateOfBirth}
              min={MIN_DATE_OF_BIRTH}
              max={today}
              aria-label="Date of birth"
              onChange={handleChange}
              onBlur={() => handleBlur('DateOfBirth')}
              className={clsx(styles.input, !fields.DateOfBirth && styles.isPlaceholder)}
            />
            {/*
              A date input has no placeholder, so the empty control would read "dd/mm/yyyy".
              The CSS hides the native value text while empty and unfocused and this span sits
              over it; the native picker icon is left alone. Ordered after the input so the
              placeholder can be dropped on focus, when the segments become editable.
            */}
            {!fields.DateOfBirth && <span className={styles.datePlaceholder}>Date of birth</span>}
          </div>
        </div>

        <ModalButton type="submit" variant="filled" disabled={isSaveDisabled}>
          {submitLabel}
        </ModalButton>

        <ModalButton type="button" variant="outline" onClick={onClose}>
          Cancel
        </ModalButton>
      </form>
    </CommonLargeModal>
  );
};

interface MemberAddModalProps {
  mode: 'add';
  onClose: () => void;
}

interface MemberEditModalProps {
  mode: 'edit';
  member: Member;
  onClose: () => void;
}

export type MemberModalProps = MemberAddModalProps | MemberEditModalProps;

const MemberAddModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { fields, errors, touched, isSaveDisabled, handleChange, handleBlur, handleSave } = useAddMember(onClose);
  return (
    <MemberForm
      title="Enter member details"
      submitLabel="Add member"
      onClose={onClose}
      fields={fields}
      errors={errors}
      touched={touched}
      isSaveDisabled={isSaveDisabled}
      handleChange={handleChange}
      handleBlur={handleBlur}
      handleSave={handleSave}
    />
  );
};

const MemberEditModal: React.FC<{ member: Member; onClose: () => void }> = ({ member, onClose }) => {
  const { fields, errors, touched, isSaveDisabled, handleChange, handleBlur, handleSave } = useEditMember(member, onClose);
  return (
    <MemberForm
      title="Edit member details"
      submitLabel="Save member"
      onClose={onClose}
      fields={fields}
      errors={errors}
      touched={touched}
      isSaveDisabled={isSaveDisabled}
      handleChange={handleChange}
      handleBlur={handleBlur}
      handleSave={handleSave}
    />
  );
};

/**
 * Each hook is called from its own component, so neither is ever invoked conditionally
 * and hook order stays stable.
 */
const MemberModal: React.FC<MemberModalProps> = ({ mode, onClose, ...rest }) => {
  if (mode === 'add') {
    return <MemberAddModal onClose={onClose} />;
  }
  const { member } = rest as MemberEditModalProps;
  return <MemberEditModal member={member} onClose={onClose} />;
};

export default MemberModal;
